import { FootPrintPadHoleType, getLayerIdentifier, isCopperLayer, isSolderMaskLayer, isSolderPasteLayer, LayerOrientation, PcbLayoutFootprintPadShape, PrimitiveRole, PrimitiveType, } from "@buildwithflux/models";
import { geom2, mat3 } from "../../../common";
import { filterTruthy } from "../../../helpers/arrays";
import { createShapeFromDxfString } from "../../../shapes/dxf/createShapeFromDxfString";
import { isDxfFileName } from "../../../shapes/helpers/isDxfFileName";
import { isSvgFileName } from "../../../shapes/helpers/isSvgFileName";
import { createShapeFromSvgString } from "../../../shapes/svg/createShapeFromSvgString";
import { createSvgShape } from "../../../shapes/svg/createSvgShape";
export function createShapesForPad(node, stackup) {
    const outerCopperShape = getCopperOuterShape(node);
    const baseHoleShape = getHoleShape(node);
    const copperLayers = getCopperLayers(node, stackup);
    const mainCopperLayer = copperLayers[0];
    // We also need to chain the scale transform, which is not already included in transformRelativeToShapesLayer
    // TODO: when doing the solder paste/mask expansion consider scale correctly!
    const transform = node.bakedRules.transformRelativeToShapesLayer ?? mat3.makeIdentity();
    const transformInverse = mat3.invert(transform);
    // Copper with no hole
    if (outerCopperShape && !baseHoleShape && mainCopperLayer) {
        // TODO: here we are assuming it to be SMD, so we are returning a single copper layer with solder paste
        // Is it possible to have multiple layers instead or no solder paste?
        const solderPasteLayer = stackup.find((layer) => isSolderPasteLayer(layer) && layer.orientation === getLayerIdentifier(mainCopperLayer));
        const solderPasteShapes = (solderPasteLayer &&
            getSolderPasteShapes(node, solderPasteLayer, outerCopperShape, transform, transformInverse)) ??
            [];
        const solderMaskLayerTop = stackup.find((layer) => isSolderMaskLayer(layer) &&
            layer.orientation === getLayerIdentifier(mainCopperLayer) &&
            layer.orientation === LayerOrientation.top);
        const solderMaskLayerBottom = stackup.find((layer) => isSolderMaskLayer(layer) &&
            layer.orientation === getLayerIdentifier(mainCopperLayer) &&
            layer.orientation === LayerOrientation.bottom);
        const solderMaskShapes = getSolderMaskShapes(node, solderMaskLayerTop, solderMaskLayerBottom, outerCopperShape, undefined, transform, transformInverse);
        const copperShape = {
            ...outerCopperShape,
            role: PrimitiveRole.copper,
            transform: mat3.multiply(transform, outerCopperShape.transform),
            transformInverse: mat3.multiply(outerCopperShape.transformInverse, transformInverse),
            layerId: mainCopperLayer.uid,
        };
        return filterTruthy([copperShape, ...solderPasteShapes, ...solderMaskShapes]);
    }
    // Copper with hole
    if (outerCopperShape && baseHoleShape) {
        // NOTE: if we have an hole we always spawn the top/bottom pads and solder mask opening
        // Also we don't spawn solder paste as that's for SMD only
        // TODO: The note above it's actually not correct! We also want to spawn solder paste
        // for through hole components if there is a solder paste expansion, starting from 0
        // (see https://www.figma.com/design/KJtwglIeAa2BZ10qySohru/Pad-%26-Via-Specs?node-id=13-61859&t=SAO1PjxzJaYWwvQM-0)
        // We are not doing it now since it's not supported yet in gerber and it poses some challenges with layers
        const solderMaskLayerTop = stackup.find((layer) => isSolderMaskLayer(layer) && layer.orientation === LayerOrientation.top);
        const solderMaskLayerBottom = stackup.find((layer) => isSolderMaskLayer(layer) && layer.orientation === LayerOrientation.bottom);
        const solderMaskShapes = getSolderMaskShapes(node, solderMaskLayerTop, solderMaskLayerBottom, outerCopperShape, baseHoleShape, transform, transformInverse);
        const copperShapes = copperLayers.map((layer) => ({
            role: PrimitiveRole.copper,
            type: PrimitiveType.Difference,
            primitiveA: { ...outerCopperShape, role: PrimitiveRole.copper, layerId: layer.uid },
            primitiveB: { ...baseHoleShape, role: PrimitiveRole.copper, layerId: layer.uid },
            layerId: layer.uid,
            transform,
            transformInverse,
        }));
        const holeShape = {
            ...baseHoleShape,
            transform: mat3.multiply(transform, baseHoleShape.transform),
            transformInverse: mat3.multiply(baseHoleShape.transformInverse, transformInverse),
            layerId: null,
            role: PrimitiveRole.layoutHole,
        };
        return filterTruthy([...copperShapes, ...solderMaskShapes, holeShape]);
    }
    // Hole Only
    if (!outerCopperShape && baseHoleShape) {
        // TODO: Also here we also want to support solder mask opening (not paste)
        // see https://www.figma.com/design/KJtwglIeAa2BZ10qySohru/Pad-%26-Via-Specs?node-id=13-61859&t=SAO1PjxzJaYWwvQM-0
        return [
            {
                ...baseHoleShape,
                transform: mat3.multiply(transform, baseHoleShape.transform),
                transformInverse: mat3.multiply(baseHoleShape.transformInverse, transformInverse),
                layerId: null,
                role: PrimitiveRole.layoutHole,
            },
        ];
    }
    return [];
}
function getCopperLayers(node, stackup) {
    const isSMD = !node.bakedRules.hole ||
        node.bakedRules.hole.holeType === FootPrintPadHoleType.SurfaceMountDevice ||
        node.bakedRules.hole.holeType === FootPrintPadHoleType.testPinOrCardEdgeConnector;
    if (isSMD) {
        const mainLayer = stackup.find((layer) => isCopperLayer(layer) && getLayerIdentifier(layer) === node.bakedRules.layer);
        return mainLayer ? [mainLayer] : [];
    }
    else {
        const matchingLayers = stackup.filter((layer) => isCopperLayer(layer) &&
            (node.bakedRules.connectedLayers?.includes("All") ||
                node.bakedRules.connectedLayers?.includes(getLayerIdentifier(layer))));
        return matchingLayers;
    }
}
function getCopperOuterShape(node) {
    const hasCopper = !node.bakedRules.hole || node.bakedRules.hole.holeType !== FootPrintPadHoleType.nonPlatedHole;
    if (!hasCopper) {
        return null;
    }
    // Will be multiplied by the caller, sets only scale
    const scale = node.bakedRules.scale;
    const transform = mat3.makeScale(scale ? scale.x : 1, scale ? scale.y : 1);
    const transformInverse = mat3.invert(transform);
    const transformTwice = mat3.makeScale(scale ? scale.x * scale.x : 1, scale ? scale.y * scale.y : 1);
    const transformTwiceInverse = mat3.invert(transformTwice);
    // If valid asset data is contained in bakedRules then attempt shape creation from that data - asset data takes
    // precedence in all cases
    if (node.bakedRules.asset?.payloadFileName && node.bakedRules.asset?.payloadAsBase64) {
        let threeShape = undefined;
        if (isSvgFileName(node.bakedRules.asset.payloadFileName)) {
            threeShape = createShapeFromSvgString(atob(node.bakedRules.asset.payloadAsBase64));
        }
        else if (isDxfFileName(node.bakedRules.asset.payloadFileName)) {
            threeShape = createShapeFromDxfString(atob(node.bakedRules.asset.payloadAsBase64), {
                center: false,
            });
        }
        if (!threeShape)
            return null;
        const points = threeShape.getPoints();
        return {
            type: PrimitiveType.ApproxPolygon,
            points,
            // Scale is applied twice when using custom shapes (??)
            transform: transformTwice,
            transformInverse: transformTwiceInverse,
            convex: geom2.isConvexFromPoints(points),
        };
    }
    if (node.bakedRules.padShape === PcbLayoutFootprintPadShape.circular) {
        const isSquare = node.bakedRules.size.x === node.bakedRules.size.y;
        if (isSquare) {
            return {
                type: PrimitiveType.Circle,
                radius: node.bakedRules.size.x / 2,
                transform,
                transformInverse,
            };
        }
        else {
            return {
                type: PrimitiveType.Oblong,
                width: node.bakedRules.size.x,
                height: node.bakedRules.size.y,
                transform,
                transformInverse,
            };
        }
    }
    if (node.bakedRules.padShape === PcbLayoutFootprintPadShape.rectangle) {
        const hasCornerRadius = node.bakedRules.cornerRadius &&
            (node.bakedRules.cornerRadius.topLeft > 0 ||
                node.bakedRules.cornerRadius.bottomLeft > 0 ||
                node.bakedRules.cornerRadius.topRight > 0 ||
                node.bakedRules.cornerRadius.bottomRight > 0);
        if (hasCornerRadius && node.bakedRules.cornerRadius) {
            return {
                type: PrimitiveType.RoundedRectangle,
                width: node.bakedRules.size.x,
                height: node.bakedRules.size.y,
                radiusTopLeft: node.bakedRules.cornerRadius.topLeft,
                radiusBottomLeft: node.bakedRules.cornerRadius.bottomLeft,
                radiusTopRight: node.bakedRules.cornerRadius.topRight,
                radiusBottomRight: node.bakedRules.cornerRadius.bottomRight,
                transform,
                transformInverse,
            };
        }
        else {
            return {
                type: PrimitiveType.Rectangle,
                width: node.bakedRules.size.x,
                height: node.bakedRules.size.y,
                transform,
                transformInverse,
            };
        }
    }
    if (!node.bakedRules.padShape)
        return null;
    const customPadShapeThree = createSvgShape(node.bakedRules.padShape);
    if (!customPadShapeThree)
        return null;
    const points = customPadShapeThree.getPoints();
    return {
        type: PrimitiveType.ApproxPolygon,
        points,
        // Scale is applied twice when using custom shapes (??)
        transform: transformTwice,
        transformInverse: transformTwiceInverse,
        convex: geom2.isConvexFromPoints(points),
    };
}
function getHoleShape(node) {
    const hole = node.bakedRules.hole;
    const hasHole = hole?.holeType === FootPrintPadHoleType.nonPlatedHole ||
        hole?.holeType === FootPrintPadHoleType.platedThroughHole;
    const holeHasArea = hasHole && hole.holeSize.x > 0 && hole.holeSize.y > 0;
    if (!hole || !hasHole || !holeHasArea) {
        return null;
    }
    // Invariant from scale
    const transform = mat3.makeTranslation(hole.holePosition.x, hole.holePosition.y);
    const transformInverse = mat3.invert(transform);
    const isSquare = hole.holeSize.x === hole.holeSize.y;
    if (isSquare) {
        return {
            type: PrimitiveType.Circle,
            radius: hole.holeSize.x / 2,
            transform,
            transformInverse,
        };
    }
    else {
        return {
            type: PrimitiveType.Oblong,
            width: hole.holeSize.x,
            height: hole.holeSize.y,
            transform,
            transformInverse,
        };
    }
}
function getSolderPasteShapes(node, solderPasteLayer, outerCopperShape, transform, transformInverse) {
    const expansion = node.bakedRules.solderPasteMaskExpansion;
    const unexpanded = {
        ...outerCopperShape,
        layerId: solderPasteLayer.uid,
        role: PrimitiveRole.solderPaste,
        transform: mat3.multiply(transform, outerCopperShape.transform),
        transformInverse: mat3.multiply(outerCopperShape.transformInverse, transformInverse),
    };
    const emptyTransform = mat3.makeIdentity();
    if (expansion !== undefined && expansion !== 0) {
        return [
            {
                type: PrimitiveType.Expansion,
                layerId: solderPasteLayer.uid,
                role: PrimitiveRole.solderPaste,
                primitive: unexpanded,
                expansion,
                transform: emptyTransform,
                transformInverse: emptyTransform,
            },
        ];
    }
    else {
        return [unexpanded];
    }
}
function getSolderMaskShapes(node, 
// Layers can also not exist (think of a single layer board)
solderMaskLayerTop, solderMaskLayerBottom, outerCopperShape, baseHoleShape, transform, transformInverse) {
    const expansionTop = node.bakedRules.solderMaskExpansion?.topExpansion ?? 0;
    const expansionBottom = node.bakedRules.solderMaskExpansion?.bottomExpansion ?? 0;
    const expansionFromHoleEdge = node.bakedRules.solderMaskExpansion?.expansionFromTheHoleEdge ?? 0;
    const emptyTransform = mat3.makeIdentity();
    const holeShapeGen = (layer, baseHoleShape) => expansionFromHoleEdge === 0
        ? {
            ...baseHoleShape,
            role: PrimitiveRole.solderMaskOpening,
            layerId: layer.uid,
        }
        : {
            type: PrimitiveType.Expansion,
            primitive: {
                ...outerCopperShape,
                role: PrimitiveRole.solderMaskOpening,
                layerId: layer.uid,
            },
            role: PrimitiveRole.solderMaskOpening,
            layerId: layer.uid,
            expansion: expansionFromHoleEdge,
            transform: emptyTransform,
            transformInverse: emptyTransform,
        };
    const shapeTop = solderMaskLayerTop &&
        (baseHoleShape
            ? {
                type: PrimitiveType.Difference,
                primitiveA: expansionTop === 0
                    ? {
                        ...outerCopperShape,
                        role: PrimitiveRole.solderMaskOpening,
                        layerId: solderMaskLayerTop.uid,
                    }
                    : {
                        type: PrimitiveType.Expansion,
                        role: PrimitiveRole.solderMaskOpening,
                        layerId: solderMaskLayerTop.uid,
                        expansion: expansionTop,
                        transform: emptyTransform,
                        transformInverse: emptyTransform,
                        primitive: {
                            ...outerCopperShape,
                            role: PrimitiveRole.solderMaskOpening,
                            layerId: solderMaskLayerTop.uid,
                        },
                    },
                primitiveB: holeShapeGen(solderMaskLayerTop, baseHoleShape),
                role: PrimitiveRole.solderMaskOpening,
                layerId: solderMaskLayerTop.uid,
                transform,
                transformInverse,
            }
            : {
                ...outerCopperShape,
                role: PrimitiveRole.solderMaskOpening,
                layerId: solderMaskLayerTop.uid,
                transform: mat3.multiply(transform, outerCopperShape.transform),
                transformInverse: mat3.multiply(outerCopperShape.transformInverse, transformInverse),
            });
    const shapeBottom = solderMaskLayerBottom &&
        (baseHoleShape
            ? {
                type: PrimitiveType.Difference,
                primitiveA: expansionBottom === 0
                    ? {
                        ...outerCopperShape,
                        role: PrimitiveRole.solderMaskOpening,
                        layerId: solderMaskLayerBottom.uid,
                    }
                    : {
                        type: PrimitiveType.Expansion,
                        role: PrimitiveRole.solderMaskOpening,
                        layerId: solderMaskLayerBottom.uid,
                        expansion: expansionBottom,
                        transform: emptyTransform,
                        transformInverse: emptyTransform,
                        primitive: {
                            ...outerCopperShape,
                            role: PrimitiveRole.solderMaskOpening,
                            layerId: solderMaskLayerBottom.uid,
                        },
                    },
                primitiveB: holeShapeGen(solderMaskLayerBottom, baseHoleShape),
                role: PrimitiveRole.solderMaskOpening,
                layerId: solderMaskLayerBottom.uid,
                transform,
                transformInverse,
            }
            : {
                ...outerCopperShape,
                role: PrimitiveRole.solderMaskOpening,
                layerId: solderMaskLayerBottom.uid,
                transform: mat3.multiply(transform, outerCopperShape.transform),
                transformInverse: mat3.multiply(outerCopperShape.transformInverse, transformInverse),
            });
    return filterTruthy([shapeTop, shapeBottom]);
}
//# sourceMappingURL=createPrimitivesForPad.js.map